import { effect, inject, signal, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { filter } from 'rxjs';

/**
 * Abstract base class for dialogs that need to handle unsaved changes.
 *
 * Provides:
 * - `warningContext` signal ('close' | 'tab' | null) for showing the warning state
 * - `tryClose()` — call from the close button; shows warning if dirty
 * - `dismissWarning()` — cancel the warning and stay
 * - `discardAndClose()` — discard changes and close
 *
 * Subclass must implement `isDirty: Signal<boolean>`.
 *
 * Usage in template:
 * ```html
 * <mat-dialog-actions align="end" [class.cipo-bg--warn-400]="warningContext()">
 *   @if (warningContext()) {
 *     <span class="cipo-text--warn-900 m-r-sm">{{ "general.discardMessage" | translate }}</span>
 *     <button mat-stroked-button (click)="dismissWarning()">{{ "actions.cancel" | translate }}</button>
 *     <button mat-stroked-button color="warn" (click)="discardAndClose()">{{ "actions.discard" | translate }}</button>
 *   } @else {
 *     <button mat-button (click)="tryClose()">{{ "actions.close" | translate }}</button>
 *     <!-- save button here -->
 *   }
 * </mat-dialog-actions>
 * ```
 */
export abstract class DirtyDialogBase {
  protected abstract isDirty: Signal<boolean>;

  warningContext = signal<'close' | 'tab'>(null);
  protected readonly dialogRef = inject(MatDialogRef);

  constructor() {
    effect(() => {
      this.dialogRef.disableClose = this.isDirty();
      if (!this.isDirty()) {
        this.warningContext.set(null);
      }
    });

    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.tryClose());

    this.dialogRef
      .keydownEvents()
      .pipe(
        filter(event => event.key === 'Escape'),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.tryClose());
  }

  tryClose(): void {
    if (this.isDirty()) {
      this.warningContext.set('close');
    } else {
      this.dialogRef.close(this.getCloseResult());
    }
  }

  dismissWarning(): void {
    this.warningContext.set(null);
  }

  discardAndClose(): void {
    this.dialogRef.close(this.getCloseResult());
  }

  protected getCloseResult(): unknown {
    return undefined;
  }
}
